import type { StatusEffectId, TowerEffectSpec } from '../../model/effects'
import {
  runtimeStatusToV2,
  statusCategories,
  type StatusApplicationResultKind,
  type StatusCategory
} from './meta'

export interface StatusHostTraits {
  immuneStatuses?: StatusEffectId[]
  immuneStatusCategories?: StatusCategory[]
  /** 1 = identity. <1 shortens duration (resistance). */
  statusDurationMultiplier?: number
  /** Per-category duration scale (CONTROL / DOT / DEBUFF). */
  statusCategoryDurationMultipliers?: Partial<Record<StatusCategory, number>>
  /** Whether the status id is already active. */
  hasStatus: (id: StatusEffectId) => boolean
}

export interface StatusApplicationResult {
  kind: StatusApplicationResultKind
  /** Duration after resistance (0 if not applied). */
  appliedDuration: number
  statusId: StatusEffectId
}

/**
 * Pure status application resolution.
 * Does not mutate — callers apply on APPLIED/REFRESHED.
 * ACTIVE enemies: no immunities, duration ×1, REFRESH policy.
 */
export function resolveStatusApplication(
  traits: StatusHostTraits,
  spec: TowerEffectSpec
): StatusApplicationResult {
  const id = spec.id
  if (traits.immuneStatuses?.includes(id)) {
    return { kind: 'IMMUNE', appliedDuration: 0, statusId: id }
  }
  const cats = statusCategories(id)
  if (traits.immuneStatusCategories?.some((c) => cats.includes(c))) {
    return { kind: 'IMMUNE', appliedDuration: 0, statusId: id }
  }

  const mult = traits.statusDurationMultiplier ?? 1
  if (!Number.isFinite(mult) || mult < 0) {
    return { kind: 'REJECTED', appliedDuration: 0, statusId: id }
  }

  let categoryMult = 1
  const catMap = traits.statusCategoryDurationMultipliers
  if (catMap) {
    for (const c of cats) {
      const v = catMap[c]
      if (typeof v === 'number' && Number.isFinite(v)) {
        categoryMult = Math.min(categoryMult, Math.max(0, v))
      }
    }
  }

  // Duration resistance: multipliers applied to duration. 0 → fully resisted.
  const appliedDuration = spec.duration * mult * categoryMult
  if (appliedDuration <= 0) {
    return { kind: 'RESISTED', appliedDuration: 0, statusId: id }
  }

  void runtimeStatusToV2 // keep mapping exported for telemetry
  if (traits.hasStatus(id)) {
    return { kind: 'REFRESHED', appliedDuration, statusId: id }
  }
  return { kind: 'APPLIED', appliedDuration, statusId: id }
}
